"use client";

import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "@scott/ui/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { useId } from "react";
import { Body } from "../body";
import { CloseIcon } from "../icon/icons/close-icon";
import { TickIcon } from "../icon/icons/tick-icon";
import { Label } from "../label";

const checkboxVariants = cva("size-[15px] font-paragraph text-neutral-dark", {
  variants: {
    variant: {
      sm: "",
      md: "",
      lg: "",
    },
    status: {
      error: "",
      success: "",
      none: "",
    },
  },
  defaultVariants: {
    variant: "md",
    status: "none",
  },
});

// Helper functions to avoid nested ternaries
function getBorderColor(
  status?: "error" | "success",
  disabled?: boolean
): string {
  if (disabled) {
    return "border-neutral-medium data-[state=checked]:border-neutral-medium";
  }
  if (status === "error") {
    return "border-danger-medium";
  }
  if (status === "success") {
    return "border-success-medium";
  }
  return "border-black data-[state=checked]:border-black";
}

function getIndicatorColor(
  status?: "error" | "success",
  disabled?: boolean
): string {
  if (disabled) {
    return "border-neutral-medium bg-neutral-medium text-neutral-medium";
  }
  if (status === "error") {
    return "border-danger-medium bg-danger-medium text-danger-medium";
  }
  if (status === "success") {
    return "border-success-medium bg-success-medium text-success-medium";
  }
  return "bg-black";
}

function getTextColor(
  status?: "error" | "success",
  disabled?: boolean
): string {
  if (disabled) {
    return "text-neutral-medium";
  }
  if (status === "error") {
    return "text-danger-medium";
  }
  if (status === "success") {
    return "text-success-medium";
  }
  return "text-neutral-darkest";
}

function getTextColorSecondary(
  status?: "error" | "success",
  disabled?: boolean
): string {
  if (disabled) {
    return "text-neutral-medium";
  }
  if (status === "error") {
    return "text-danger-medium";
  }
  if (status === "success") {
    return "text-success-medium";
  }
  return "text-neutral-dark";
}

function getTextSize(variant?: "sm" | "md" | "lg" | null): string {
  if (variant === "sm") {
    return "text-sm";
  }
  if (variant === "md") {
    return "text-base";
  }
  if (variant === "lg") {
    return "text-lg";
  }
  return "text-base"; // Default
}

export interface CheckboxProps
  extends VariantProps<typeof checkboxVariants>,
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  asChild?: boolean;
  description?: string;
  label?: string | React.ReactNode;
  isRequired?: boolean;
  status?: "error" | "success";
  message?: string;
  labelClassName?: string;
}

const Checkbox = ({
  className,
  variant,
  status,
  message,
  isRequired,
  description,
  label,
  labelClassName,
  ref,
  id,
  ...props
}: CheckboxProps & {
  ref?: React.RefObject<React.ElementRef<typeof CheckboxPrimitive.Root> | null>;
}) => {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-x-2">
        <CheckboxPrimitive.Root
          className={cn(
            "flex shrink-0 items-center justify-center rounded-[2px] border bg-white p-0 transition-[background-color,border-color,outline-color] duration-150 ease-out hover:bg-neutral-lighter focus-visible:outline-2 focus-visible:outline-black focus-visible:outline-offset-2 active:bg-neutral-light disabled:cursor-not-allowed disabled:bg-neutral-lighter disabled:hover:bg-neutral-lighter",
            checkboxVariants({ variant, className }),
            getBorderColor(status, props.disabled)
          )}
          id={controlId}
          ref={ref}
          {...props}
        >
          <CheckboxPrimitive.Indicator className="flex items-center justify-center">
            <span
              className={cn(
                "block size-[11px] rounded-[1px]",
                getIndicatorColor(status, props.disabled)
              )}
            />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        <div className="flex flex-col items-start gap-y-1">
          {label && (
            <Label
              className={cn(
                "flex text-wrap font-normal",
                props.disabled ? "cursor-not-allowed" : "cursor-pointer",
                getTextColor(status, props.disabled),
                getTextSize(variant),
                labelClassName
              )}
              htmlFor={controlId}
            >
              {label}
              <Body
                className={cn(
                  "ml-1",
                  getTextColorSecondary(status, props.disabled),
                  getTextSize(variant)
                )}
              >
                {isRequired ? "*" : ""}
              </Body>
            </Label>
          )}
          <div className="flex flex-col items-start">
            {status === "error" && (
              <Body className={cn("text-danger-medium", getTextSize(variant))}>
                {description}
              </Body>
            )}
            {status === "success" && (
              <Body className={cn("text-button-success", getTextSize(variant))}>
                {description}
              </Body>
            )}
            {props.disabled && description && (
              <Body className={cn("text-neutral-medium", getTextSize(variant))}>
                {description}
              </Body>
            )}
            {!(props.disabled || status) && description && (
              <Body className={cn("text-neutral-dark", getTextSize(variant))}>
                {description}
              </Body>
            )}
          </div>
        </div>
      </div>
      {status && (
        <div>
          {status === "error" && (
            <span className="flex items-center gap-1">
              <div className="flex h-3 w-3 items-center justify-center rounded-full bg-error p-1">
                <CloseIcon className="h-2 w-2 text-white" size="sm" />
              </div>
              <Body className="text-error" size="xs">
                {message}
              </Body>
            </span>
          )}
          {status === "success" && (
            <span className="flex items-center gap-1">
              <TickIcon className="h-3 w-3 text-button-success" size="sm" />
              <Body className="text-button-success" size="xs">
                {message}
              </Body>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
